import { ref, get } from "firebase/database"
import { NextPriceScheme, NextRegionScheme } from "@coast/utils"

import { sdk9db } from "../firebase"
import { NextFullRegion, NextSuperRegion, Coordinates } from "../types"
import { regionFromCoords } from "./locationUtils"

export async function getFullRegions() {
  const tasks = []

  tasks.push(
    get(ref(sdk9db, "regionSchemeV2")).then((regionsSnap) => regionsSnap.val()),
  )

  tasks.push(
    get(ref(sdk9db, "priceSchemesV2")).then((priceSchemesSnap) =>
      priceSchemesSnap.val(),
    ),
  )

  tasks.push(
    get(ref(sdk9db, "superRegionsV2")).then((superRegionsSnap) =>
      superRegionsSnap.val(),
    ),
  )

  const [regionSchemes, priceSchemes, superRegions] = await Promise.all(tasks)
  const regions: { [region: string]: NextFullRegion } = {}

  Object.keys(regionSchemes).forEach((region) => {
    const regionScheme: NextRegionScheme = regionSchemes[region]
    const priceScheme: NextPriceScheme | undefined =
      priceSchemes[regionScheme.priceSchemeId ?? ""]
    const superRegion: NextSuperRegion | undefined =
      superRegions[regionScheme.superRegionId ?? ""]

    regions[region] = {
      ...regionScheme,
      priceScheme,
      superRegion,
    }
  })

  return { regions }
}

export async function getFullRegion(regionId: string): Promise<NextFullRegion> {
  const regionScheme = await get(
    ref(sdk9db, `regionSchemeV2/${regionId}`),
  ).then((regionsSnap) => regionsSnap.val() as NextRegionScheme)
  const priceSchemeTask = !regionScheme?.priceSchemeId
    ? null
    : get(ref(sdk9db, `priceSchemesV2/${regionScheme.priceSchemeId}`)).then(
        (priceSchemesSnap) => priceSchemesSnap.val() as NextPriceScheme,
      )
  const superRegionTask = !regionScheme?.superRegionId
    ? null
    : get(ref(sdk9db, `superRegionsV2/${regionScheme.superRegionId}`)).then(
        (superRegionsSnap) => superRegionsSnap.val() as NextSuperRegion,
      )
  const priceScheme = priceSchemeTask ? await priceSchemeTask : undefined
  const superRegion = superRegionTask ? await superRegionTask : undefined

  return {
    ...regionScheme,
    priceScheme,
    superRegion,
  }
}

export async function getSuperRegion(
  coords?: Coordinates | null,
): Promise<string | null> {
  if (!coords) return null
  const region = await regionFromCoords(coords)
  if (!region) return null
  const { superRegionId } = region
  if (!superRegionId) return null
  return get(ref(sdk9db, `superRegionsV2/${superRegionId}`)).then(
    (superRegionSnap) => {
      const superRegion = superRegionSnap.val()
      if (!superRegion) return null
      return superRegion.name
    },
  )
}

export async function getSuperRegionById(
  superRegionId: string,
): Promise<NextSuperRegion | null> {
  return get(ref(sdk9db, `superRegionsV2/${superRegionId}`)).then(
    (superRegionSnap) => superRegionSnap.val() as NextSuperRegion,
  )
}

export const STATE_COORDS_MAP: { [state: string]: Coordinates } = {
  ca: { lat: 36.7855, lng: -119.8769 },
  co: { lat: 39.8013, lng: -105.0872 },
  il: { lat: 40.5864, lng: -89.1385 },
  in: { lat: 41.4754, lng: -87.0946 },
  nv: { lat: 41.9931, lng: -120.0064 },
  mo: { lat: 38.6533, lng: -90.3157 },
  tx: { lat: 32.7983, lng: -96.8116 },
}
